/**
 * Persistent cache + background warming for AOI coverage results.
 *
 * Why this exists: computing coverage for a large AOI is not the slow part —
 * shipping the clipped polygons is. A 7554-vertex area over 48 h is ~10.5 MB with
 * geometry and ~81 KB without (8.3 KB gzipped). Warming therefore fetches the
 * geometry-free form, which is small enough to cache in IndexedDB and fast enough
 * to prefetch for every area a user has, so opening one is instant instead of a
 * visible "recompute".
 *
 * Cache entries are keyed by the AOI's own content, the time window, the satellite
 * set and whether geometry was requested — so a different area, a moved window or
 * a geometry-bearing request never reads a stale neighbour's result. Entries carry
 * a TTL and are served stale-while-revalidate: the UI paints immediately from cache
 * and quietly replaces the data when the refetch lands.
 */

const DB_NAME = 'overpass.aoiWarmCache';
const DB_VERSION = 1;
const STORE = 'coverage';

/** Predictions move as elements refresh (~6 h) and GFS cycles (6 h); an hour is well inside both. */
const DEFAULT_TTL_MS = 60 * 60 * 1000;

/** Keep the store bounded — oldest entries are evicted first. */
const MAX_ENTRIES = 60;

export interface WarmCacheEntry {
  key: string;
  storedAt: number;
  /** The `/api/coverage` document, verbatim. */
  document: Record<string, unknown>;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('storedAt', 'storedAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    // Private-browsing and quota failures are not errors here: the cache is an
    // optimisation, and every caller falls back to a live fetch.
    request.onerror = () => resolve(null);
  });
  return dbPromise;
}

/**
 * A stable, order-independent digest of the AOI geometry.
 *
 * JSON.stringify of the same geometry is stable within a session, which is all
 * this needs — a false miss costs one refetch, never a wrong answer.
 */
export function hashAoi(geojson: unknown): string {
  const text = JSON.stringify(geojson);
  // FNV-1a: short, dependency-free, and collision-safe enough for a local cache key.
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${hash.toString(36)}-${text.length.toString(36)}`;
}

export function cacheKey(options: {
  aoiHash: string;
  startMs: number;
  endMs: number;
  noradIds: number[];
  includeGeometry: boolean;
}): string {
  // Window bounds are bucketed to the minute so that a clock tick between two
  // otherwise identical requests does not miss the cache.
  const start = Math.floor(options.startMs / 60000);
  const end = Math.floor(options.endMs / 60000);
  const sats = [...options.noradIds].sort((a, b) => a - b).join('.');
  return `${options.aoiHash}|${start}|${end}|${options.includeGeometry ? 'g' : 'n'}|${sats}`;
}

export async function readCache(key: string, ttlMs = DEFAULT_TTL_MS): Promise<{
  document: Record<string, unknown>;
  stale: boolean;
} | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    let request: IDBRequest<WarmCacheEntry | undefined>;
    try {
      request = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
    } catch {
      resolve(null);
      return;
    }
    request.onsuccess = () => {
      const entry = request.result;
      if (!entry) {
        resolve(null);
        return;
      }
      resolve({ document: entry.document, stale: Date.now() - entry.storedAt > ttlMs });
    };
    request.onerror = () => resolve(null);
  });
}

export async function writeCache(key: string, document: Record<string, unknown>): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({ key, storedAt: Date.now(), document } satisfies WarmCacheEntry);
    await new Promise<void>((resolve) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    return; // quota exceeded or store missing — the caller still has its data
  }
  await evictOldest();
}

async function evictOldest(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const countRequest = store.count();
    countRequest.onsuccess = () => {
      const excess = countRequest.result - MAX_ENTRIES;
      if (excess <= 0) return;
      let removed = 0;
      const cursorRequest = store.index('storedAt').openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor || removed >= excess) return;
        cursor.delete();
        removed += 1;
        cursor.continue();
      };
    };
  } catch {
    /* eviction is best-effort */
  }
}

export async function clearWarmCache(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    db.transaction(STORE, 'readwrite').objectStore(STORE).clear();
  } catch {
    /* nothing to clear */
  }
}
